// dialogs/crawler-dialog/crawler-dialog.component.ts
//
// Reactive create/edit dialog for crawler integrations. One root FormGroup
// holds the common fields (name, recurrence, trademarks, config) and a
// per-mode sub-group (single/excel/discovery) that is attached/detached on
// crawlerMode change.

import { Component, ElementRef, Inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as ExcelJS from 'exceljs';
import { ToastrService } from 'ngx-toastr';
import { ButtonToggleProps } from 'src/app/components/button-toggle/button-toggle.component';
import { Option } from 'src/app/components/select/select.component';
import {
  CrawlerConfig,
  CrawlerDiscoveryStrategyType,
  CrawlerType,
  IntegrationWithCrawler,
  MAX_DISCOVERY_DEPTH,
  MAX_PAGES_OPTIONS_MIN,
  MAX_PAGES_OPTIONS_MAX,
} from 'src/app/models/integration/crawler';
import { Trademark } from 'src/app/models/trademark';
import {
  CrawlerCreationParams,
  CrawlerMode,
  IntegrationService,
} from 'src/app/services/integration.service';
import { UtilsService } from 'src/app/services/utils.service';
import {
  ConfigForm,
  CrawlerFormFactory,
  CrawlerFormGroup,
  DiscoveryForm,
  ExcelForm,
  ScreenshotFormat,
  SingleForm,
} from './crawler-dialog.forms';
import { AclService } from '../../services/acl.service';
import { CrawlerUrlBalance, OrganizationService } from '../../services/organization.service';
import { Role } from '../../models/user';

interface CrawlerDialogData {
  mode: 'create' | 'edit';
  integration?: IntegrationWithCrawler;
}

type AppError = { value: boolean; message?: string };

const MODE_BY_TYPE: Record<CrawlerType, CrawlerMode> = {
  [CrawlerType.SINGLE_URL]: 'single',
  [CrawlerType.EXCEL_BATCH]: 'excel',
  [CrawlerType.DISCOVERY]: 'discovery',
};

@Component({
  selector: 'app-crawler-dialog',
  templateUrl: './crawler-dialog.component.html',
  styleUrls: ['./crawler-dialog.component.scss'],
})
export class CrawlerDialogComponent {
  readonly mode: 'create' | 'edit';
  readonly loading = signal(false);
  readonly showAdvancedOptions = signal(false);
  readonly excelFileName = signal<string | undefined>(undefined);
  readonly excelColumns = signal<Option[]>([]);
  readonly columnParseFailed = signal(false);
  readonly parsingColumns = signal(false);
  // The organisation's exploration allowance for the year, read fresh when the dialog opens.
  readonly balance = signal<CrawlerUrlBalance | undefined>(undefined);

  form!: CrawlerFormGroup;
  private readonly forms: CrawlerFormFactory;

  readonly recurrences = [
    { value: 'once', label: 'DATES.ONCE' },
    { value: 'week', label: 'DATES.WEEKLY' },
    { value: 'month', label: 'DATES.MONTHLY' },
  ];

  // Discovery depth levels 1..MAX_DISCOVERY_DEPTH, rendered as a select.
  readonly depthLevels: Option[] = [...Array(MAX_DISCOVERY_DEPTH).keys()].map((i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  readonly formatOptions = [
    { value: 'jpeg', label: 'JPEG' },
    { value: 'png', label: 'PNG' },
  ];

  readonly crawlerModes: (ButtonToggleProps & { value: CrawlerMode; hint: string; role: Role | undefined })[] = [
    { selected: true, disabled: false, role: undefined, label: 'INTEGRATIONS.WEBSITE_CRAWLER.SINGLE_URL', hint: 'INTEGRATIONS.WEBSITE_CRAWLER.SINGLE_URL_HINT', value: 'single', icon: 'link' },
    { selected: false, disabled: true, role: Role.MANAGER, label: 'INTEGRATIONS.BACKLINK_CRAWLER.TITLE', hint: 'INTEGRATIONS.BACKLINK_CRAWLER.HINT', value: 'discovery', icon: 'travel_explore' },
    { selected: false,  disabled: true, role: Role.ADMIN, label: 'INTEGRATIONS.WEBSITE_CRAWLER.EXCEL_FILE', hint: 'INTEGRATIONS.WEBSITE_CRAWLER.EXCEL_FILE_HINT', value: 'excel', icon: 'table_chart' },
  ];

  readonly discoveryStrategies: (ButtonToggleProps & { value: CrawlerDiscoveryStrategyType })[] = [
    { selected: true, label: 'INTEGRATIONS.BACKLINK_CRAWLER.STRATEGIES.GENERIC', value: 'GENERIC', icon: 'language' },
    { selected: false, label: 'INTEGRATIONS.BACKLINK_CRAWLER.STRATEGIES.AMAZON', value: 'AMAZON', icon: 'shopping_bag' },
  ];

  protected readonly MAX_DISCOVERY_DEPTH = MAX_DISCOVERY_DEPTH;
  protected readonly MAX_PAGES_OPTIONS_MIN = MAX_PAGES_OPTIONS_MIN;
  protected readonly MAX_PAGES_OPTIONS_MAX = MAX_PAGES_OPTIONS_MAX;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: CrawlerDialogData,
    private readonly dialogRef: MatDialogRef<CrawlerDialogComponent>,
    private readonly integrationService: IntegrationService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly utils: UtilsService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly aclService: AclService,
    private readonly organizationService: OrganizationService,
  ) {
    this.mode = this.data.mode;
    this.forms = new CrawlerFormFactory(this.urlValidator(), this.mode);
    this.form = this.forms.root();
    this.setModeGroup('single');

    this.form.controls.crawlerMode.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((mode) => this.setModeGroup(mode));

    if (this.mode === 'edit') {
      this.patchFromIntegration();
    }

    this.organizationService.myQuotas()
      .then((quotas) => this.balance.set(quotas.crawlerUrls))
      .catch(() => this.balance.set(undefined));
  }

  // Shares of the allowance for the gauge, in percent.
  balanceShare(part: 'consumed' | 'reserved'): number {
    const balance = this.balance();
    if (!balance || balance.subscribed <= 0) return 0;
    return Math.min(100, (balance[part] / balance.subscribed) * 100);
  }

  // A client's ceiling above what is left: the exploration will stop at what is left.
  exceedsBalance(): boolean {
    const balance = this.balance();
    const maxPages = this.discoveryGroup?.controls.maxPages.value;
    return !!balance && balance.subscribed > 0 && !this.aclService.hasPlatformRights()
      && typeof maxPages === 'number' && maxPages > balance.available;
  }

  // user profile
  isSuperAdmin(): boolean {
    return this.aclService.isSuperAdmin();
  }

  isAdmin(): boolean {
    return this.aclService.isAdmin();
  }

  isManagerOrAbove(): boolean {
    return this.aclService.isManagerOrAbove();
  }

  // Capture settings belong to the platform team; the backend applies the defaults for anyone else.
  canTuneCapture(): boolean {
    return this.hasAccess(Role.SUPERADMIN);
  }

  // Exploration is an option of the organisation (an exploration allowance); the platform team always has it.
  canExplore(): boolean {
    return this.hasAccess(Role.SUPERADMIN) || this.organizationService.quotas.crawlerUrls.subscribed > 0;
  }

  // The role allows the mode, but the organisation does not have the exploration option.
  isExplorationNotEnabled(mode: CrawlerMode): boolean {
    return mode === 'discovery' && !this.canExplore();
  }

  isModeEnabled(mode: CrawlerMode): boolean {
    const selectedMode = this.crawlerModes.find(el => el.value === mode);
    return !!selectedMode && (!selectedMode.disabled || this.hasAccess(selectedMode.role))
      && !this.isExplorationNotEnabled(mode);
  }

  hasAccess(role: Role | undefined): boolean {
    if (!role) return true;

    switch (role) {
      case Role.SUPERADMIN:
        // The platform team's rights: inside a client organisation, only when acting as Admin.
        return this.aclService.hasPlatformRights();
      case Role.ADMIN:
        return this.isAdmin() || this.isSuperAdmin();
      case Role.MANAGER:
        return this.isManagerOrAbove();
      default:
        return false;
    }
  }

  // ── Form construction ────────────────────────────────────────────────────

  private urlValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null; // emptiness handled by Validators.required
      if (!this.utils.validateUrlPattern(value)) return { url: true };
      return this.utils.isInternalUrl(value) ? { notPublic: true } : null;
    };
  }

  private setModeGroup(mode: CrawlerMode): void {
    (['single', 'excel', 'discovery'] as const).forEach((key) => {
      if (this.form.contains(key)) {
        this.form.removeControl(key);
      }
    });

    // Leaving excel mode: drop any parsed-file state so a stale filename/columns
    // don't linger when the user comes back.
    if (mode !== 'excel') {
      this.excelFileName.set(undefined);
      this.excelColumns.set([]);
      this.columnParseFailed.set(false);
      this.parsingColumns.set(false);
    }

    switch (mode) {
      case 'single':
        this.form.addControl('single', this.forms.single());
        break;
      case 'excel':
        this.form.addControl('excel', this.forms.excel());
        break;
      case 'discovery':
        this.form.addControl('discovery', this.forms.discovery());
        break;
    }
  }

  // ── Edit-mode hydration ──────────────────────────────────────────────────
  //
  // Backend only supports rename ({ name }) today, so every control except
  // `name` is disabled. The full form is still rendered so the UI is ready
  // when an update endpoint is exposed (see CRAWLER_API_MIGRATION.md).

  private patchFromIntegration(): void {
    const data = this.data.integration;
    if (!data) return;
    const { integration, crawler } = data;
    const mode = MODE_BY_TYPE[crawler.type];

    this.form.patchValue({
      name: integration.name,
      crawlerMode: mode,
      recurrence: crawler.recurrence,
      trademarks: (crawler.trademarks ?? []) as unknown as Trademark[],
      config: {
        screenshotFormat: crawler.config?.screenshotFormat ?? 'jpeg',
        quality: crawler.config?.quality ?? 80,
        viewport: {
          width: crawler.config?.viewport?.width ?? 1280,
          height: crawler.config?.viewport?.height ?? 720,
        },
        concurrency: crawler.config?.concurrency ?? 2,
        delay: crawler.config?.delay ?? 2000,
        batchSize: crawler.config?.batchSize ?? 100,
        pageTimeout: crawler.config?.pageTimeout ?? 240000,
      },
    });

    // setModeGroup already ran via the crawlerMode patch above; fill the spec.
    if (mode === 'single' && crawler.type === CrawlerType.SINGLE_URL) {
      this.singleGroup?.patchValue({ url: crawler.spec.url });
    } else if (mode === 'excel' && crawler.type === CrawlerType.EXCEL_BATCH) {
      this.excelGroup?.patchValue({ urlColumn: crawler.spec.urlColumn });
      this.excelFileName.set(crawler.spec.excelFileName);
    } else if (mode === 'discovery' && crawler.type === CrawlerType.DISCOVERY) {
      this.discoveryGroup?.patchValue({
        url: crawler.spec.url,
        depth: crawler.spec.discoveryDepth,
        maxPages: crawler.spec.maxDiscoverablePages,
        strategy: crawler.spec.strategy,
      });
    }

    // countryCodes is a common root field, stored top-level on the crawler.
    this.form.patchValue({ countryCodes: crawler.countryCodes ?? [] });

    this.form.disable({ emitEvent: false });
    this.form.controls.name.enable({ emitEvent: false });
  }

  // ── Template accessors ─────────────────────────────────────────────────────

  get crawlerMode(): CrawlerMode {
    return this.form.controls.crawlerMode.value;
  }

  get configGroup(): FormGroup<ConfigForm> {
    return this.form.controls.config;
  }

  get singleGroup(): FormGroup<SingleForm> | undefined {
    return this.form.controls.single;
  }

  get excelGroup(): FormGroup<ExcelForm> | undefined {
    return this.form.controls.excel;
  }

  get discoveryGroup(): FormGroup<DiscoveryForm> | undefined {
    return this.form.controls.discovery;
  }

  get discoveryStrategy(): CrawlerDiscoveryStrategyType | undefined {
    return this.discoveryGroup?.controls.strategy.value;
  }

  get screenshotFormat(): ScreenshotFormat {
    return this.configGroup.controls.screenshotFormat.value;
  }

  // Maps a control's first validation error to the { value, message } shape the
  // app-* components expect. Only surfaces once the control is touched.
  inputError(
    control: AbstractControl | null | undefined,
    messages: Record<string, string>,
    params?: Record<string, unknown>,
  ): AppError {
    if (!control || control.disabled || !control.touched || !control.errors) {
      return { value: false };
    }
    const key = Object.keys(control.errors)[0];
    return { value: true, message: this.translate.instant(messages[key] ?? 'ERRORS.GENERIC', params) };
  }

  // ── Toggles ────────────────────────────────────────────────────────────────

  setCrawlerMode(mode: CrawlerMode): void {
    if (this.crawlerMode === mode) return;
    if (!this.isModeEnabled(mode)) {
      return;
    }
    this.form.controls.crawlerMode.setValue(mode);
  }

  onDiscoveryStrategyChange(index: number): void {
    const group = this.discoveryGroup;
    if (!group) return;
    const strategy = this.discoveryStrategies[index].value;
    group.controls.strategy.setValue(strategy);
    this.forms.syncDiscoveryStrategyControls(group, strategy);
  }

  // ── Excel file ───────────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const control = this.excelGroup?.controls.excelFile;
    if (!input.files || input.files.length === 0 || !control) return;

    this.excelColumns.set([]);
    this.columnParseFailed.set(false);

    const file = input.files[0];
    const isExcel = ['.xlsx', '.xls'].some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!isExcel) {
      control.setErrors({ fileType: true });
      control.markAsTouched();
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      control.setErrors({ fileSize: true });
      control.markAsTouched();
      return;
    }

    control.setValue(file);
    control.setErrors(null);
    this.excelFileName.set(file.name);
    void this.parseExcelColumns(file);
  }

  removeFile(input?: HTMLInputElement): void {
    this.excelGroup?.controls.excelFile.setValue(null);
    this.excelGroup?.controls.urlColumn.setValue('');
    this.excelFileName.set(undefined);
    this.excelColumns.set([]);
    this.columnParseFailed.set(false);
    this.parsingColumns.set(false);
    // Reset the native <input> value so re-picking the SAME file still fires (change).
    if (input) input.value = '';
  }

  // Reads the uploaded workbook's first row to offer the real column headers as a
  // dropdown — users pick an actual header instead of typing it (and confusing it
  // with the A/B/C column letter). Falls back to the free-text input when the file
  // can't be read (e.g. legacy .xls, which ExcelJS doesn't parse).
  private async parseExcelColumns(file: File): Promise<void> {
    const control = this.excelGroup?.controls.urlColumn;
    control?.setValue('');
    this.excelColumns.set([]);
    this.columnParseFailed.set(false);
    this.parsingColumns.set(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) throw new Error('No worksheet found');

      const headers: string[] = [];
      worksheet.getRow(1).eachCell({ includeEmpty: false }, (cell) => {
        const name = (cell.text ?? '').trim();
        if (name) headers.push(name);
      });
      if (headers.length === 0) throw new Error('No header row found');

      const columns = Array.from(new Set(headers));
      this.excelColumns.set(columns.map((name) => ({ label: name, value: name })));

      const detected = this.detectUrlColumn(columns);
      if (detected) control?.setValue(detected);
    } catch (err) {
      console.error('Could not read Excel columns', err);
      this.excelColumns.set([]);
      this.columnParseFailed.set(true);
    } finally {
      this.parsingColumns.set(false);
    }
  }

  // Best-effort guess of the URL column from the header names (always row 1):
  // match a keyword in a header, else auto-pick only when there's a single column
  // (zero risk). Otherwise leave it empty so the user picks consciously.
  private detectUrlColumn(headers: string[]): string | undefined {
    const keywords = ['url', 'lien', 'link', 'adresse', 'address', 'site', 'web', 'href'];
    const byName = headers.find((h) => keywords.some((k) => h.toLowerCase().includes(k)));
    if (byName) return byName;

    return headers.length === 1 ? headers[0] : undefined;
  }

  // ── Submit ───────────────────────────────────────────────────────────────────

  onCancel(): void {
    this.dialogRef.close({ created: false, edited: false });
  }

  // `launch`: start the crawl (or, for an exploration, the discovery) right after the creation.
  submit(launch = false): void {
    if (this.mode === 'edit') {
      void this.onEdit();
    } else {
      void this.onCreate(launch);
    }
  }

  // Reveals and scrolls to the first invalid field so the user knows what to fix,
  // rather than facing a silently-disabled submit button. The (collapsible) config
  // group is expanded first when the offending control lives inside it.
  private focusFirstInvalidControl(): void {
    if (this.configGroup.invalid) {
      this.showAdvancedOptions.set(true);
    }
    // Defer so the view reflects the touched/invalid classes (and the freshly
    // expanded advanced panel) before we query the DOM.
    setTimeout(() => {
      const host = this.elementRef.nativeElement;
      const invalid = host.querySelector<HTMLElement>('[formcontrolname].ng-invalid');
      if (!invalid) return;
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalid.querySelector<HTMLElement>('input, textarea, select')?.focus({ preventScroll: true });
    });
  }

  // Numerics are coerced with Number() because app-input writes its bound value
  // back as a string even when the control is typed as number.
  private collectConfig(): Partial<CrawlerConfig> {
    const config = this.configGroup.getRawValue();
    return {
      screenshotFormat: config.screenshotFormat,
      quality: Number(config.quality),
      viewport: {
        width: Number(config.viewport.width),
        height: Number(config.viewport.height),
      },
      concurrency: Number(config.concurrency),
      delay: Number(config.delay),
      batchSize: Number(config.batchSize),
      pageTimeout: Number(config.pageTimeout),
    };
  }

  private async onCreate(launch: boolean): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalidControl();
      return;
    }

    const raw = this.form.getRawValue();
    const config = this.collectConfig();
    const trademarks = raw.trademarks
      .map((t) => t._id)
      .filter((id): id is string => !!id);

    let params: CrawlerCreationParams;
    switch (raw.crawlerMode) {
      case 'single':
        params = {
          crawlerMode: 'single',
          name: raw.name,
          url: raw.single!.url,
          recurrence: raw.recurrence,
          trademarks,
          countryCodes: raw.countryCodes,
          config,
        };
        break;
      case 'excel':
        params = {
          crawlerMode: 'excel',
          name: raw.name,
          excelFile: raw.excel!.excelFile!,
          urlColumn: raw.excel!.urlColumn,
          recurrence: raw.recurrence,
          trademarks,
          countryCodes: raw.countryCodes,
          config,
        };
        break;
      case 'discovery': {
        const discovery = raw.discovery!;
        params = {
          crawlerMode: 'discovery',
          name: raw.name,
          url: discovery.url,
          strategy: discovery.strategy,
          recurrence: raw.recurrence,
          trademarks,
          countryCodes: raw.countryCodes,
          maxPages: Number(discovery.maxPages),
          config,
        };
        // depth is irrelevant to the AMAZON strategy.
        if (discovery.strategy !== 'AMAZON') {
          params.depth = Number(discovery.depth);
        }
        break;
      }
    }

    let integrationId: string;
    try {
      this.loading.set(true);
      const created = await this.integrationService.createCrawler(params);
      integrationId = created.integration._id;
      this.toastr.success(this.translate.instant('INTEGRATIONS.INTEGRATION_CREATED', { name: raw.name }));
    } catch (err: any) {
      this.utils.toastError(err);
      this.loading.set(false);
      return;
    }

    // A refused launch (quota, rights) leaves the integration created: its page tells why and can relaunch it.
    if (launch) {
      const discovery = raw.crawlerMode === 'discovery';
      try {
        await this.integrationService.trigger(integrationId, false, discovery ? 'discover' : 'crawl');
        this.toastr.success(this.translate.instant(
          discovery ? 'INTEGRATIONS.DISCOVERY.LAUNCHED' : 'INTEGRATIONS.CRAWL_URLS.CRAWL_LAUNCHED',
        ));
      } catch (err: unknown) {
        this.utils.toastError(err);
      }
    }
    this.loading.set(false);
    this.dialogRef.close({ created: true, edited: false, integrationId });
  }

  private async onEdit(): Promise<void> {
    const nameControl = this.form.controls.name;
    const id = this.data.integration?.integration._id;
    if (nameControl.invalid || !id) {
      nameControl.markAsTouched();
      this.focusFirstInvalidControl();
      return;
    }

    try {
      this.loading.set(true);
      await this.integrationService.rename(id, nameControl.value);
      this.toastr.success(this.translate.instant('INTEGRATIONS.INTEGRATION_UPDATED', { name: nameControl.value }));
      this.dialogRef.close({ created: false, edited: true });
    } catch (err: any) {
      this.utils.toastError(err);
    } finally {
      this.loading.set(false);
    }
  }
}
